/**
 * GridCrash — Sidebar (persistent column on desktop, stacked block on phones)
 *
 * A persistent control-center column beside the board: live session
 * stats, badge progress, and the global ladder. Below 1100px it stacks
 * full-width beneath the board instead of sitting beside it (pure CSS),
 * but stays populated at every width.
 */

import type { GameState } from "../game/engine.js";
import { getEarnedAchievements, ACHIEVEMENTS } from "../game/achievements.js";
import { getLifetimeScore, getNextUnlockThreshold, getTierProgress } from "../game/unlocks.js";
import { getTodaysQuests, getStreak } from "../game/quests.js";
import { getEquippedTitle, getShowcase } from "../game/titles.js";
import { fetchTopScores, type GlobalScore } from "../game/global_leaderboard.js";
import { formatTime } from "../game/leaderboard.js";

const BREAKPOINT = "(min-width: 1100px)";
const LADDER_CACHE_MS = 60_000;

export class Sidebar {
  private container: HTMLElement;
  private statsEl!: HTMLElement;
  private questsEl!: HTMLElement;
  private badgesEl!: HTMLElement;
  private ladderEl!: HTMLElement;
  private media: MediaQueryList;

  private currentTierKey = "";
  private currentTierLabel = "";
  private ladderCache = new Map<string, { at: number; scores: GlobalScore[] }>();
  private lastPhase = "";

  constructor(container: HTMLElement) {
    this.container = container;
    this.media = window.matchMedia(BREAKPOINT);
    this.render();

    // Populate lazily when the layout first widens into view.
    this.media.addEventListener("change", () => {
      if (this.media.matches) {
        this.renderBadges();
        void this.renderLadder();
      }
    });
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="side-card" aria-label="Session stats">
        <h3 class="side-card__title">📊 This Game</h3>
        <div class="side-card__body" id="side-stats"></div>
      </div>
      <div class="side-card" aria-label="Daily quests">
        <h3 class="side-card__title">🎯 Daily Quests</h3>
        <div class="side-card__body" id="side-quests"></div>
      </div>
      <div class="side-card" aria-label="Badge progress">
        <h3 class="side-card__title">🏅 Badges</h3>
        <div class="side-card__body" id="side-badges"></div>
      </div>
      <div class="side-card" aria-label="Global top scores">
        <h3 class="side-card__title">🌐 Global Ladder</h3>
        <div class="side-card__body" id="side-ladder"></div>
      </div>
    `;
    this.statsEl  = this.container.querySelector<HTMLElement>("#side-stats")!;
    this.questsEl = this.container.querySelector<HTMLElement>("#side-quests")!;
    this.badgesEl = this.container.querySelector<HTMLElement>("#side-badges")!;
    this.ladderEl = this.container.querySelector<HTMLElement>("#side-ladder")!;
    this.renderQuests();
    this.renderBadges();
    void this.renderLadder();
  }

  /** Called alongside hud.update on every engine state change. */
  update(state: Readonly<GameState>, minesRemaining: number): void {
    const phaseLabel =
      state.phase === "WIN"  ? "🏁 Cleared!" :
      state.phase === "LOSE" ? "💥 Blew up" :
      state.phase === "PLAY" ? "▶️ Playing" : "— Ready";

    this.statsEl.innerHTML = `
      <dl class="side-stats">
        <div><dt>Mode</dt><dd>${state.difficulty.label}</dd></div>
        <div><dt>Time</dt><dd>${formatTime(state.timeElapsed)}</dd></div>
        <div><dt>Mines left</dt><dd>${minesRemaining}</dd></div>
        <div><dt>Flags</dt><dd>${state.flagsPlaced}</dd></div>
        <div><dt>Status</dt><dd>${phaseLabel}</dd></div>
      </dl>
    `;

    // Refresh tier-scoped ladder when the mode changes, and badges +
    // ladder when a game ends (scores may have moved).
    if (state.difficulty.leaderboardKey !== this.currentTierKey) {
      this.currentTierKey = state.difficulty.leaderboardKey;
      this.currentTierLabel = state.difficulty.label;
      this.renderBadges();
      void this.renderLadder();
    }
    const ended = state.phase === "WIN" || state.phase === "LOSE";
    if (ended && this.lastPhase !== state.phase) {
      this.renderBadges();
      this.renderQuests();
      this.ladderCache.delete(this.currentTierKey);
      void this.renderLadder();
    }
    this.lastPhase = state.phase;
  }

  /** Compact read-only quest progress; claiming lives in the Quests tab. */
  renderQuests(): void {
    const streak = getStreak();
    const rows = getTodaysQuests().map(({ def, progress }) => {
      const done = progress.progress >= def.target;
      return `
        <li class="side-quest${done ? " side-quest--done" : ""}">
          <span class="side-quest__label">${done ? "✅" : "⬜"} ${def.label}</span>
          <span class="side-quest__meta">${progress.progress}/${def.target} · 🪙${def.reward}</span>
        </li>`;
    }).join("");

    this.questsEl.innerHTML = `
      <ul class="side-quests">${rows}</ul>
      ${streak.count > 0
        ? `<div class="side-quests__streak">🔥 ${streak.count}-day streak${streak.savers > 0 ? ` · 🛟×${streak.savers}` : ""}</div>`
        : ""}
    `;
  }

  private renderBadges(): void {
    const earned = getEarnedAchievements();
    const lifetime = getLifetimeScore();
    const nextThresh = getNextUnlockThreshold();
    const progressPct = Math.round(getTierProgress() * 100);

    // Showcased badges lead the row (picked in the Badges tab).
    const showcase = getShowcase();
    const pinned = showcase
      .map((id) => earned.find((a) => a.id === id))
      .filter((a): a is NonNullable<typeof a> => a !== undefined);
    const orderedBadges = [
      ...pinned,
      ...earned.filter((a) => !showcase.includes(a.id)),
    ];

    const badgeRow = orderedBadges.length > 0
      ? orderedBadges.map((a) =>
          `<span class="side-badge${showcase.includes(a.id) ? " side-badge--pinned" : ""}" title="${a.name}">${a.emoji}</span>`
        ).join("")
      : `<span class="side-empty">No badges yet — keep playing!</span>`;

    const title = getEquippedTitle();

    this.badgesEl.innerHTML = `
      ${title ? `<div class="side-title">🏷️ ${title}</div>` : ""}
      <div class="side-badges__row">${badgeRow}</div>
      <div class="side-badges__count">${earned.length} / ${ACHIEVEMENTS.length} earned</div>
      <div class="side-progress">
        <div class="side-progress__label">
          ⚡ ${lifetime.toLocaleString()} pts${nextThresh > 0 ? ` · next mascot at ${nextThresh.toLocaleString()}` : " · all mascots unlocked"}
        </div>
        <div class="side-progress__track">
          <div class="side-progress__fill" style="width:${progressPct}%"></div>
        </div>
      </div>
    `;
  }

  private async renderLadder(): Promise<void> {
    const tierKey = this.currentTierKey;
    if (!tierKey) return;

    const cached = this.ladderCache.get(tierKey);
    if (cached && Date.now() - cached.at < LADDER_CACHE_MS) {
      this.paintLadder(cached.scores);
      return;
    }

    this.ladderEl.innerHTML = `<span class="side-empty">Loading…</span>`;
    const scores = await fetchTopScores(tierKey, "alltime", 10);
    // Tier may have changed while the request was in flight.
    if (tierKey !== this.currentTierKey) return;

    this.ladderCache.set(tierKey, { at: Date.now(), scores });
    this.paintLadder(scores);
  }

  private paintLadder(scores: GlobalScore[]): void {
    if (scores.length === 0) {
      this.ladderEl.innerHTML =
        `<span class="side-empty">No global scores for ${this.currentTierLabel} yet.</span>`;
      return;
    }
    this.ladderEl.innerHTML = `
      <ol class="side-ladder">
        ${scores.map((s) => `
          <li>
            <span class="side-ladder__name">${s.name}${s.title ? ` <span class="side-ladder__title">${s.title}</span>` : ""}</span>
            <span class="side-ladder__score">${s.score.toLocaleString()}</span>
          </li>`).join("")}
      </ol>
    `;
  }
}
